import {
  TranslationStatus,
  type CreationMethod,
} from '@amzn/gbsa-helios-typescript-client';

/**
 * Response types for sub-questions.
 * PASS_FAIL is the legacy/default type (scored). All others are data-collection (non-scoring).
 */
export enum ResponseType {
  PASS_FAIL = 'PASS_FAIL',
  TEXT = 'TEXT',
  NUMERIC = 'NUMERIC',
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  YES_NO = 'YES_NO',
  USER_ALIAS = 'USER_ALIAS',
}

/**
 * Controls when a conditional sub-question is shown based on the parent question's result.
 * Only applicable when the parent question has hasConditionalSubQuestions = true.
 */
export enum ShowOn {
  PASS = 'PASS',
  FAIL = 'FAIL',
  BOTH = 'BOTH',
}

export type ChangeLogList = Array<ChangeLogEntry>; // Define specific change log type if needed
export interface ChangeLogEntry {
  version?: number;
  changedBy: string;
  changedAt?: string;
  reason?: string;
  changedFields?: string[];
}

export interface SubQuestion {
  title: string;
  subQuestionId?: string;
  orderIndex: number;
  instruction: string;
  /**
   * @deprecated Use mediaIdsMap instead for better field-level granularity
   * This field is kept for backward compatibility when reading old questions
   * New saves should only use mediaIdsMap
   */
  mediaIds?: string[];
  mediaIdsMap?: {
    instruction?: string[];
    taskDescription?: string[];
  };
  links?: DocLink[];
  taskDetails?: TaskDetails;
  score?: number;
  mediaResponses?: MediaResponse[];
  mediaResponsesMap?: {
    instruction?: MediaResponse[];
    taskDescription?: MediaResponse[];
  };
  requireCommentOnFail?: boolean;
  requireEvidenceOnFail?: boolean;
  requireCommentOnPass?: boolean;
  requireEvidenceOnPass?: boolean;
  /** ERT: Response type for this sub-question. Undefined/absent = PASS_FAIL (legacy). */
  responseType?: ResponseType;
  /** ERT: Options for SELECT/MULTI_SELECT/YES_NO types. Not applicable for other types. */
  options?: string[];
  /** ERT: When to show this sub-question based on parent result. Only for conditional subs. */
  showWhenParentIs?: ShowOn;
  /** ERT: Unconditional comment requirement for data-collection subs. */
  requireComment?: boolean;
  /** ERT: Unconditional attachment requirement for data-collection subs. */
  requireAttachment?: boolean;
}

// Main Interfaces
export interface CreateQuestionRequest {
  programId: string;
  divisionName: string;
  businessUnit: string;
  auditScore: number;
  isActive: boolean;
  defaultLanguage: string;
  defaultLanguageContent: QuestionContent;
  categories?: string[];
  docLinks?: DocLink[];
  tagsIncluded: TagMap;
  tagsExcluded?: TagMap;
  mediaIdsMap?: MediaIdsMap;
  locationsExcluded?: string[];
  subQuestions?: SubQuestion[];
  requireCommentOnFail?: boolean;
  requireEvidenceOnFail?: boolean;
  requireCommentOnPass?: boolean;
  requireEvidenceOnPass?: boolean;
  /** ERT: Whether this question has conditional sub-questions gated by parent result. */
  hasConditionalSubQuestions?: boolean;
  /** How this question was created (MANUAL vs ASTRA). Write-once provenance set
   *  on create only — NOT on update (the backend preserves it across version
   *  bumps). Omitted → the backend defaults to MANUAL. */
  creationMethod?: CreationMethod;
  createdBy: string;
}

export interface CreateQuestionResponse {
  questionId: string;
  version: number;
}

export interface UpdateQuestionRequest {
  programId: string;
  questionId: string;
  version: number; // Required for optimistic locking
  divisionName: string;
  businessUnit: string;
  auditScore?: number;
  categories?: string[];
  isActive?: boolean;
  docLinks?: DocLink[];
  defaultLanguage?: string;
  defaultLanguageContent?: QuestionContent;
  translationStatus?: TranslationStatus;
  translationVersionsMap?: TranslationVersionMap;
  tagsIncluded?: TagMap;
  tagsExcluded?: TagMap;
  mediaIdsMap?: MediaIdsMap;
  locationsExcluded?: string[];
  subQuestions?: SubQuestion[];
  changeLog?: ChangeLogList;
  requireCommentOnFail?: boolean;
  requireEvidenceOnFail?: boolean;
  requireCommentOnPass?: boolean;
  requireEvidenceOnPass?: boolean;
  /** ERT: Whether this question has conditional sub-questions gated by parent result. */
  hasConditionalSubQuestions?: boolean;
  updatedBy: string;
  reason: string;
}
export interface UpdateQuestionResponse {
  question: Question;
}

export interface Question {
  questionId: string;
  version: number;
  isLatestVersion: boolean;
  division: string;
  divisionName: string;
  categories?: string[];
  businessUnit: string;
  program: string;
  auditScore: number;
  docLinks?: DocLink[];
  isActive: boolean;
  defaultLanguage: string;
  defaultLanguageContent: QuestionContent;
  translationStatus?: TranslationStatus;
  translationVersionsMap?: TranslationVersionMap;
  tagsIncluded: TagMap;
  tagsExcluded?: TagMap;
  mediaIdsMap?: MediaIdsMap;
  locationsExcluded?: string[];
  subQuestions?: SubQuestion[];
  changeLog?: ChangeLogList;
  requireCommentOnFail?: boolean;
  requireEvidenceOnFail?: boolean;
  requireCommentOnPass?: boolean;
  requireEvidenceOnPass?: boolean;
  /** ERT: Whether this question has conditional sub-questions gated by parent result. */
  hasConditionalSubQuestions?: boolean;
  createdAt?: number;
  createdBy: string;
  lastUpdatedAt?: number;
  lastUpdatedBy: string;
  localizedContent: LocalizedContent;
  mediaResponses: MediaResponses;
  localeTranslationContentMap?: {
    [key: string]: TranslationContent;
  };
  isExcludedFromTemplate?: boolean;
}

export interface TranslationContent {
  subQuestionsTranslationMap: {
    [key: string]: SubQuestionTranslation;
  };
  questionTranslation?: QuestionContent;
  createdAt?: string;
  createdBy?: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
  translationVersion?: string;
  /** The question version this translation was validated/overwritten against */
  validFromQuestionVersion?: string;
  /** Whether this translation is stale (created for an older question version). */
  stale?: boolean | null;
  /** Whether this translation was manually edited by a user. */
  userEdited?: boolean | null;
}

export interface SubQuestionTranslation {
  instruction: string;
  taskDetails: TaskDetails;
  title: string;
}

export interface SearchQuestionTranslationEntry {
  content: QuestionContent;
  stale?: boolean | null;
  userEdited?: boolean | null;
}

export interface Translations {
  [key: string]: SearchQuestionTranslationEntry;
}

export interface MediaResponses {
  questionText?: MediaResponse[];
  passCriteria?: MediaResponse[];
  failCriteria?: MediaResponse[];
  auditInstruction?: MediaResponse[];
  justification?: MediaResponse[];
  taskDescription?: MediaResponse[];
}
export interface MediaResponse {
  media: {
    mediaId: string;
    entityType?: string;
    s3Key?: string;
    fileName?: string;
    extension?: string;
    fileSize?: number;
    program?: string;
    businessUnit?: string;
    createdAt?: string;
    createdBy?: string;
    lastUpdatedAt?: string;
    lastUpdatedBy?: string;
  };
  presignedUrl: string;
}

export interface LocalizedContent {
  content: QuestionContent;
  language: string;
  translations?: Translations;
}

export interface DocLink {
  title?: string;
  link?: string;
  /**
   * `true` marks the source SOP an Astra (AuditWhiz) question was generated
   * from — system-attached provenance, shown badged and not removable.
   * Absent/false = an ordinary author-added link. A question may carry several.
   *
   * The backend stores whatever the client sends, so every layer that maps doc
   * links MUST carry this through (dropping it loses the marker permanently).
   * Manual authoring never sets it; a copy demotes it (see `toAuthorDocLink`).
   */
  isSourceSop?: boolean;
}

export interface TagMap {
  [key: string]: string[];
}

export interface MediaIdsMap {
  auditInstruction?: string[];
  justification?: string[];
  taskDescription?: string[];
  questionText?: string[];
  passCriteria?: string[];
  failCriteria?: string[];
  [key: string]: string[] | undefined; // Allow dynamic keys for subquestion fields
}

export interface QuestionContent {
  questionDetails: QuestionDetails;
  taskDetails?: TaskDetails;
  subQuestions?: SubQuestion[];
}

export interface QuestionDetails {
  title: string;
  questionText: string;
  auditInstruction: string;
  passCriteria: string;
  failCriteria: string;
  justification: string;
}

export interface TaskDetails {
  taskId?: string;
  taskName: string;
  taskDescription: string;
  taskDurationDays?: number;
}

export interface TranslationVersionMap {
  [key: string]: TranslationVersionInfo;
}

export interface TranslationVersionInfo {
  translationVersion: string;
  validFromQuestionVersion: string;
}

export interface ListQuestionsRequest {
  programId: string;
  businessUnit: string;
  divisionName: string;
  maxResults?: number;
  nextToken?: string;
  isDeleted: boolean;
}

export interface ListQuestionsResponse {
  questions: Question[];
  nextToken?: string;
}

export interface SearchQuestionRequest {
  businessUnitId: string;
  divisionName?: string;
  isActive?: boolean;
  categories?: string[];
  templateId?: string;
  tagFilters?: TagMap;
  excludedTagFilters?: TagMap;
  keyword?: string;
  questionIds?: string[]; // Array for API
  questionIdsText?: string; // String for UI persistence (to preserve commas while typing)
  language?: string;
  additionalLanguages?: string[];
  maxResults?: number;
  nextToken?: string;
  applyTemplateExclusions?: boolean;
}

export interface SearchQuestionResponse {
  questions: Question[];
  nextToken?: string;
}

export interface GetQuestionRequest {
  programId: string;
  questionId: string;
  divisionName: string;
  businessUnit: string;
  language?: string;
  version?: string;
}
export interface GetQuestionResponse {
  question: Question;
}
